import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { HttpError } from "./queryClient";
import { useTanStackSettings } from "./TanStackProvider";

export enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

async function apiFetch<T = unknown>(
  path: string,
  {
    headers = {},
    method = HttpMethod.GET,
    body,
    ...init
  }: Omit<RequestInit, "method" | "body"> & {
    method?: HttpMethod;
    body?: unknown;
  } = {},
): Promise<T> {
  const requestHeaders = new Headers({
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
  });

  const requestInit: RequestInit = {
    credentials: "include",
    ...init,
    headers: requestHeaders,
    method: method,
  };

  if (body != null && method !== HttpMethod.GET) {
    requestInit.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  const response = await fetch(path, requestInit);
  let responseBody: unknown;

  try {
    responseBody = await response.json();
  } catch {
    responseBody = { url: response.url, statusText: response.statusText };
  }

  if (!response.ok) {
    const error = new Error(`Request failed: ${response.statusText}`) as HttpError;
    error.status = response.status;
    error.body = responseBody;
    throw error;
  }

  return responseBody as T;
}

type UseQueryTanStackOptions<T> = Partial<UseQueryOptions<T, HttpError, T>>;

/**
 * Simplifies usage of `useQuery`
 *
 * @template T The expected type of the data returned from the API.
 * @param path The API endpoint to fetch data from.
 * @param options `useQuery` options.
 * @returns The result of the TanStack Query `useQuery` hook.
 *
 * @example
 * const { data: publicData, isLoading: isPublicLoading } = useQueryTanStack<MyDataType>("/api/public-data");
 */
export const useQueryTanStack = <T>(
  path: string,
  {
    queryKey = [path],
    queryFn = ({ signal }) => apiFetch<T>(path, { method: HttpMethod.GET, signal }),
    ...options
  }: UseQueryTanStackOptions<T> = {},
) => {
  const { staleTime } = useTanStackSettings();

  return useQuery<T, HttpError>({
    staleTime,
    ...options,
    queryFn,
    queryKey,
  });
};

/**
 * Handles POST, PUT, and DELETE requests with anti-forgery token from context.
 *
 * @param path The path to send the request to, or a function deriving it from the mutation variables
 * @param method HTTP method
 * @param options Additional TanStack Query mutation options
 * @returns TanStack Query mutation result
 *
 * @example
 * const mutation = useMutationTanStack("/api/endpoint", HttpMethod.POST, {
 *   onSuccess: () => {
 *     invalidateQueryTanStack("/api/data");
 *   }
 * });
 *
 * mutation.mutate({ data: "value" });
 */
export const useMutationTanStack = <TResponse, TRequest>(
  path: string | ((variables: TRequest) => string),
  method: Exclude<HttpMethod, HttpMethod.GET>,
  options?: Omit<UseMutationOptions<TResponse, HttpError, TRequest>, "mutationFn">,
  getHeaders?: () => Record<string, string>,
) => {
  const { antiForgeryToken } = useTanStackSettings();

  return useMutation<TResponse, HttpError, TRequest>({
    mutationKey: typeof path === "string" ? [method, path] : [method],
    mutationFn: (variables) =>
      apiFetch<TResponse>(typeof path === "string" ? path : path(variables), {
        method,
        headers: {
          ...(antiForgeryToken ? { [antiForgeryToken.name]: antiForgeryToken.value } : undefined),
          ...getHeaders?.(),
        },
        body: variables,
      }),
    ...options,
  });
};
